import { inject, Injectable } from '@angular/core';
import { type AuthenticatedConfig } from './app.config';
import { type ImportConfig } from '@bizzdesign/feature-data-management';
import { environment } from '../environments/environment';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, tap } from 'rxjs';
import { type ILogObj, Logger } from 'tslog';
import { RedirectUrlService } from '@bizzdesign/auth';
import { AuthClient, AuthTokenProvider } from '@bizzdesign/sdk-auth';
import { InvitationAnalyticsService } from '@bizzdesign/shared/services';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private readonly logger: Logger<ILogObj> = new Logger({
    overwrite: {
      transportFormatted: (
        logMetaMarkup: string,
        logArgs: unknown[],
        logErrors: string[],
      ): void => {
        console.log(...logArgs);
      },
    },
  });

  private readonly authClient = inject(AuthClient);
  private readonly authTokenProvider = inject(AuthTokenProvider);
  private readonly http = inject(HttpClient);
  private readonly redirectUrlService = inject(RedirectUrlService);
  private readonly invitationAnalyticsService = inject(
    InvitationAnalyticsService,
  );

  private config: AuthenticatedConfig | null = null;

  async loadAuthenticatedConfig() {
    try {
      const isAuthenticated = await this.authClient.isAuthenticated();
      if (!isAuthenticated) {
        const currentPath = window.location.pathname + window.location.search;
        this.redirectUrlService.storeRedirectUrl(currentPath);

        const invitationData =
          this.invitationAnalyticsService.getStoredInvitationData();
        if (invitationData) {
          this.invitationAnalyticsService.trackLoginRedirectStarted(
            invitationData,
          );
        }
      }

      await this.authClient.ensureAuthenticated();
      const token = await this.authTokenProvider.getToken();
      await firstValueFrom(
        this.http
          .get<AuthenticatedConfig>(environment.configUrl, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          })
          .pipe(
            tap((response: AuthenticatedConfig) => {
              this.config = response;
            }),
          ),
      );
    } catch (error) {
      this.logger.error({
        context: 'ConfigService',
        msg: 'Failed to load configuration',
        error: error,
      });
    }
  }

  get importConfig(): ImportConfig {
    return {
      nuvoLicenseKey:
        this.config?.importNuvoLicenseKey ||
        environment.importNuvoLicenseKey ||
        '',
      nuvoDeveloperMode:
        this.config?.importNuvoDeveloperMode ??
        environment.importNuvoDeveloperMode,
    };
  }
}
